import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';

import { cn } from '@/lib/utils';

const TooltipProvider = TooltipPrimitive.Provider;

const Tooltip = TooltipPrimitive.Root;

const TooltipTrigger = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Trigger>,
  TooltipPrimitive.TooltipTriggerProps & React.RefAttributes<HTMLButtonElement>
>(({ children, className, asChild = true, ...props }, ref) => (
  <TooltipPrimitive.Trigger {...props} type="button" asChild={asChild}>
    {children}
  </TooltipPrimitive.Trigger>
));

type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

interface TooltipContentProps
  extends React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content> {
  position?: TooltipPosition;
}

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  TooltipContentProps
>(
  (
    { className, position = 'top', sideOffset = 5, children, ...props },
    ref,
  ) => (
    <TooltipPrimitive.Content
      ref={ref}
      side={position}
      sideOffset={sideOffset}
      avoidCollisions={true}
      collisionPadding={8}
      className={cn(
        'relative z-50 inline-block max-w-xs px-3 py-1.5',
        'rounded-md bg-border text-primary-foreground',
        'text-center text-sm',
        'animate-in fade-in-0 zoom-in-95',
        'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
        'data-[side=bottom]:slide-in-from-top-2',
        'data-[side=left]:slide-in-from-right-2',
        'data-[side=right]:slide-in-from-left-2',
        'data-[side=top]:slide-in-from-bottom-2',
        className,
      )}
      {...props}
    >
      {children}
      {/* <TooltipArrow position={position} /> */}
    </TooltipPrimitive.Content>
  ),
);
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

const TooltipArrow = ({ position }: { position: TooltipPosition }) => {
  const arrowPositionStyles = {
    top: 'bottom-full rotate-180',
    bottom: 'top-full',
    left: 'right-full rotate-90',
    right: 'left-full -rotate-90',
  };

  return (
    <svg
      className={cn('absolute z-50', arrowPositionStyles[position])}
      width="11"
      height="5"
      viewBox="0 0 11 5"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M0 0L11 0L5.5 5L0 0Z" fill="#292929" />
    </svg>
  );
};

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };

type TooltipWrapperProps = {
  content: React.ReactNode;
  children: React.ReactNode;
  offset?: number;
  align?: 'left' | 'center';
  className?: string;
  fixed?: boolean;
  direction?: 'top' | 'left' | 'right';
  disabled?: boolean;
};

const getStyles = ({
  direction = 'top',
  offset = 1.2,
}: Pick<TooltipWrapperProps, 'direction' | 'offset'>) => {
  switch (direction) {
    case 'top':
      return { bottom: `${offset * 100}%` };
    case 'left':
      return { right: `${offset * 100}%` };
    case 'right':
      return { left: `${offset * 100}%` };
  }
};

export default function TooltipWrapper({
  children,
  disabled = false,
  content,
  align = 'center',
  className,
  fixed,
  direction = 'top',
  offset = 1.2,
}: TooltipWrapperProps) {
  return (
    <div
      className={cn('group/tooltip', !fixed && 'relative')}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
      {!disabled ? (
        fixed ? (
          <div
            style={getStyles({ direction, offset })}
            className={cn(
              'pointer-events-none absolute flex items-center justify-center opacity-0 transition-opacity group-hover/tooltip:pointer-events-auto group-hover/tooltip:opacity-100',
              className,
            )}
          >
            <div className="fixed w-80 max-w-max translate-x-3 rounded-md bg-border px-3 py-2 text-center text-sm text-foreground">
              {content}
            </div>
          </div>
        ) : (
          <div
            style={getStyles({ direction, offset })}
            className={cn(
              'pointer-events-none absolute z-50 w-56 max-w-max rounded-md bg-border px-3 py-2 text-center font-sans text-sm tracking-wide text-foreground opacity-0 transition-opacity group-hover/tooltip:pointer-events-auto group-hover/tooltip:opacity-100 sm:w-80',
              {
                'left-1/2 -translate-x-1/2':
                  align === 'center' &&
                  direction !== 'left' &&
                  direction !== 'right',
                'left-0': align === 'left',
                'top-1/2 -translate-y-1/2':
                  align === 'center' &&
                  (direction === 'left' || direction === 'right'),
              },
              className,
            )}
          >
            {content}
          </div>
        )
      ) : (
        <></>
      )}
    </div>
    // <TooltipProvider delayDuration={0}>
    //   <Tooltip>
    //     <TooltipTrigger asChild>{children}</TooltipTrigger>
    //     <TooltipContent>{content}</TooltipContent>
    //   </Tooltip>
    // </TooltipProvider>
  );
}
